'use client';

import { Popover as PopoverPrimitive } from 'radix-ui';
import type * as React from 'react';

import { cn } from '../lib/utils';

/**
 * Mirror of the shadcn Popover in `apps/web/src/components/ui/popover.tsx`.
 *
 * A MIRROR rather than a move, on purpose. That directory is shadcn-managed and
 * must not be edited (G9), and turning it into a re-export shim would make the
 * next `shadcn add popover` silently clobber the package boundary. The app keeps
 * its generated copy for the rest of the app; the package carries its own so it
 * has no `@/`-alias dependency and stays buildable on its own. The two are
 * byte-identical apart from the `cn` import path — if shadcn regenerates the
 * app's copy, re-apply the same diff here.
 *
 * `Portal` is the load-bearing part for the pickers built on this: the content
 * renders at `document.body`, so a panel opened inside a scrolling column
 * (the report builder's config rail) floats over the page instead of being
 * clipped by the scrollport or pushing the layout down.
 */
function Popover({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
  className,
  align = 'center',
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'z-50 w-72 origin-(--radix-popover-content-transform-origin) rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-hidden',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

function PopoverAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger };
